import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from '../finance/shared';

const PJvsCLTCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [cltGross, setCltGross] = useState('5.000,00');
  const [cltDiscountRate, setCltDiscountRate] = useState('20,00');
  const [pjGross, setPjGross] = useState('7.000,00');
  const [pjTaxRate, setPjTaxRate] = useState('10,00');
  const [resultState, setResultState] = useState<any>(null);

  const calculate = () => {
    const clt = parseFloat(parseBrazilianNumber(cltGross)) || 0;
    const cltD = parseFloat(parseBrazilianNumber(cltDiscountRate)) / 100;
    const pj = parseFloat(parseBrazilianNumber(pjGross)) || 0;
    const pjT = parseFloat(parseBrazilianNumber(pjTaxRate)) / 100;
    if (clt <= 0 || pj <= 0 || cltD < 0 || cltD >= 1 || pjT < 0 || pjT >= 1) return;

    // Líquido mensal
    const cltNet = clt * (1 - cltD);
    const pjNet = pj * (1 - pjT);

    // Projeção anual CLP (contando 13º e 1/3 de férias + FGTS)
    const cltAnnual = (cltNet * 13.33) + (clt * 0.08 * 12);
    const pjAnnual = pjNet * 12;

    const diff = (pjAnnual / 12) - (cltAnnual / 12);

    const res = { cltNet, pjNet, cltAnnual, pjAnnual, diff };
    setResultState(res);

    onResultChange([
      { label: 'Líquido CLT Mensal', value: `R$ ${cltNet.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Líquido PJ Mensal', value: `R$ ${pjNet.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Equivalente CLT/mês', value: `R$ ${(cltAnnual / 12).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <FormField label="Salário CLT Bruto (R$)" value={cltGross} onChange={setCltGross} required isNumeric type="text" />
          <FormField label="Descontos CLT (%)" value={cltDiscountRate} onChange={setCltDiscountRate} required isNumeric type="text" helpText="INSS + IRPF est." />
          <FormField label="Valor PJ Bruto (R$)" value={pjGross} onChange={setPjGross} required isNumeric type="text" />
          <FormField label="Impostos PJ (%)" value={pjTaxRate} onChange={setPjTaxRate} required isNumeric type="text" helpText="DAS + outros" />
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Comparar Modalidades
          </button>
        </div>

        {resultState && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ResultCard title="Equiv. CLT Mensal" value={`R$ ${(resultState.cltAnnual / 12).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} description="Considerando benefícios" color="slate" />
            <ResultCard title="PJ Líquido Mensal" value={`R$ ${resultState.pjNet.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} color="indigo" />
            <ResultCard title="Vantagem PJ/mês" value={`R$ ${resultState.diff.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} color={resultState.diff >= 0 ? 'emerald' : 'rose'} />
          </div>
        )}
      </div>

      <EducationalBlock
        title="Diferenças entre PJ e CLT"
        tips={[
          'CLT oferece benefícios como 13º, Férias remuneradas e FGTS',
          'PJ geralmente tem um valor bruto maior para compensar a falta de benefícios',
          'No PJ, o profissional é responsável por seus próprios impostos (como o DAS)',
          'Considere custos extras como contador e previdência privada no PJ'
        ]}
      />
    </div>
  );
};

export default PJvsCLTCalculator;
